import { ElementRef, Renderer2, Injector, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { isEmpty, isUndefined } from 'lodash';
import * as bowser from 'bowser';
import * as zenscroll from 'zenscroll';
import { BaseComponent } from '../../base.component';
import { TooltipHandlerComponent } from '../tooltip-handler/tooltip-handler.component';
import { InformationModalService } from '../../../Services/information-model-factory/information-model-factory';
import { Subscription } from 'rxjs';

const DEFAULT = 'default';
const INFO = 'info';
const RATES = 'rates';
const DEFAULT_ICON = 'icon-default';
const QUESTION_MARK_ICON = 'icon-help';
const INFO_ICON = 'icon-info';
const RATES_ICON = 'rates-icon-info';

@Component({
	selector: 'tooltip',
	template: `
		<div
			*ngIf="this.tooltip"
			gtm-handler
			[attr.gtm]="gaLabel"
			events="click"
			handlerType="button"
			[label]="gaLabel"
			action="ButtonClick"
			class="icon {{ getIconClass() }} "
			[ngClass]="{ active: isOpen, infoBlock: type === 'info-block' }"
			(mouseover)="toggleView()"
			(touchstart)="toggleView()"
			[hidden]="!showTooltip()"
			tabindex="0"
			aria-label="Tooltip"
			(keydown)="onKeyPress($event)"
		></div>

		<toggle-visible
			*ngIf="isOpen"
			[isOpen]="isOpen"
			[ignoreElement]="el"
			(focusLost)="focusLostCallback()"
			(closeOnClick)="focusLostCallback()"
		>
			<div role="tooltip" aria-live="polite" class="help-box" [ngClass]="boxType">
				<button aria-label="Close tooltip" class="close-help-box" (click)="toggle($event)" tabindex="0">
					<i class="icon icon-close"></i>
				</button>

				<div *ngIf="type === 'small'">
					<span class="tooltip-content" [innerHtml]="content"></span>
					<span class="bold" *ngIf="shouldShowCoverageHelp"
						><br /><br />Questions?&nbsp; &nbsp;Call
						<a class="phone-number" [tabindex]="0" href="tel:{{ phoneNumber }}">{{ phoneNumber }}</a> for an expert
						recommendation and explanation</span
					>
				</div>

				<div *ngIf="type !== 'small'">
					<div class="small-box">
						<div
							class="tooltip-content"
							tabindex="0"
							[ngClass]="{ 'extra-content-height': type === 'icon-help' && showPhoneNumber && phoneNumber }"
						>
							<div class="title">{{ tooltip.title }}</div>
							<div [innerHtml]="content"></div>
							<span class="bold" *ngIf="shouldShowCoverageHelp"
								><br /><br />Questions?&nbsp; &nbsp;Call
								<a class="phone-number" [tabindex]="0" href="tel:{{ phoneNumber }}">{{ phoneNumber }}</a> for an expert
								recommendation and explanation</span
							>
						</div>

						<div
							class="show-more-button"
							*ngIf="showMoreButton"
							(click)="showMore()"
							gtm-handler
							events="click"
							handlerType="button"
							[label]="getLabel('_More')"
							action="LinkClick"
						>
							More ...
						</div>
					</div>
					<div class="call-help" *ngIf="type === 'icon-help' && showPhoneNumber && phoneNumber">
						<span>Need help?</span>

						<a
							href="tel:{{ phoneNumber || '844-416-5371' }}"
							gtm-handler
							events="click"
							handlerType="button"
							[label]="getLabel('')"
							action="LinkClick"
							tabindex="0"
							>{{ phoneNumber }}</a
						>
					</div>
				</div>
			</div>
		</toggle-visible>
	`,
	styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() title: string;
	@Input() type: string;
	@Input() tooltip: any;
	@Input() boxType: any;
	@Input() gtmData: string;
	@Input() showPhoneNumber = true;

	public gaLabel = '';
	isOpen: boolean;
	focusLost: boolean;
	isInit: boolean;
	showMoreButton: boolean;
	phoneNumber: string;
	content: any;
	tooltipSubscription: Subscription;
	shouldShowCoverageHelp: boolean;

	constructor(
		injector: Injector,
		public el: ElementRef,
		private renderer: Renderer2,
		private tooltipService: InformationModalService
	) {
		super(injector);
	}

	async ngOnInit() {
		this.tooltipSubscription = this.tooltipService.openedTooltip$.subscribe((tooltip) => {
			if (this.tooltip !== tooltip) {
				this.focusLostCallback();
			}
		});
		this.isOpen = false;
		this.focusLost = false;
		this.isInit = true;
		if (this.tooltip) {
			if (this.tooltip.attributes) {
				this.gaLabel = this.tooltip.attributes.gaEventLabel;
			}
			this.content = this.tooltip.expandedContent || this.tooltip.content;
			if (this.tooltip.title == 'Plumbing type:') {
				this.content =
					'This helps determine the material of the fresh water supply lines throughout your home. If accessible, look at the pipes connected to your water heater or what runs underneath the home.<br><br><strong>Copper:</strong> Copper pipes are rigid in construction and are bright orange when first installed. As the pipes age, the color may oxidize and turn brown. Used from the 1930s to today.<br><br><strong>Galvanized:</strong> Steel pipes dipped in a zinc coating to prevent corrosion. Galvanized pipes are gray in color and will react to a magnet (unlike copper). Installed commonly from the 1930s to the 1980s.<br><br><strong>PEX:</strong> Flexible red and blue plastic tubes commonly used from the 1970s to today as a non-corrosive alternative to copper.<br><br><strong>PVC:</strong> Rigid white plastic pipes used from the 1960s to today.<br><br><strong>Polybutylene:</strong> Flexible gray supply lines with identifiable coppper or brass clamps commonly used from the 1970s to the 1990s,but are no longer installed today.';
			}
			if (this.content.includes('{{CoverageTooltipsHelpText}}')) {
				this.content = this.content.replace('{{CoverageTooltipsHelpText}}', '');
				this.tooltip.content = this.content;
				this.shouldShowCoverageHelp = true;
			}
			this.showMoreButton = false;
		}
		const globalAttributes = this.storeService.getGlobalAttributes();
		if (globalAttributes) {
			this.phoneNumber = this.tooltip && this.tooltip.phoneNumber ? this.tooltip.phoneNumber : false;

			if (globalAttributes['fl-phone-removal']) {
				this.showPhoneNumber = false;
			}
		}

		if (this.gtmData) {
			this.renderer.setAttribute(this.el.nativeElement, 'gtm', this.gtmData.replace(/_/g, ' '));
		}
	}

	getIconClass() {
		const icon = QUESTION_MARK_ICON;
		switch (this.type) {
			case DEFAULT:
				return DEFAULT_ICON;
			case INFO:
				return INFO_ICON;
			case RATES:
				return RATES_ICON;
		}
		const bottomTooltip = 'bottom';
		return `${icon} ${bottomTooltip}`;
	}

	setConsumerType() {
		if (this.isConsumer()) {
			return 'icon-title-consumer';
		} else {
			return null;
		}
	}

	setConsumerTypeBox() {
		if (this.isConsumer()) {
			return 'help-box-consumer';
		} else {
			return null;
		}
	}

	getBoxClass(boxType) {
		const icon = boxType;
		return ` ${icon} ${this.setConsumerTypeBox()}`;
	}
	isConsumer() {
		if (!isUndefined(this.storeService.getAddOnParams().dataLayerParams)) {
			const flowtype = this.storeService.getAddOnParams().dataLayerParams['channelType'];
			if (flowtype === 'Direct') {
				return true;
			} else {
				return false;
			}
		}
		return false;
	}

	showMore() {
		this.showMoreButton = false;
		this.content = this.tooltip.expandedContent;

		if (this.isOpen && !this.checkIfRatesTooltip()) {
			this.resizeContent();
			this.scrollToTop();
		}
	}

	scrollToTop() {
		// Get body size & element & calculate offset
		const bodyRect = document.body.getBoundingClientRect();
		const elemRect = this.el.nativeElement.getBoundingClientRect();
		const offset = elemRect.top - bodyRect.top;
		const headerRect = document.getElementsByTagName('header')[0].getBoundingClientRect();

		// Wait for tooltip to render
		this.isMobile =
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'mobile' ||
			bowser.getParser(window.navigator.userAgent).getPlatformType() === 'tablet';
		setTimeout(() => {
			if (this.isMobile && screen.width <= 1000) {
				// Detect if tooltip top position is overflows the screen (including header height and padding)
				const overflow =
					this.el.nativeElement.querySelector('.help-box').getBoundingClientRect().top -
					(bodyRect.top + headerRect.height + 15);

				// Negative overflow, handle overscreen tooltips in mobile
				if (overflow < 0) {
					// Scroll to screen top
					zenscroll.toY(0, 100, () => {
						// Get tooltip text
						const contentRect = this.el.nativeElement.querySelector('.tooltip-content').getBoundingClientRect();
						// Get tooltip wrapper
						const wrapRect = this.el.nativeElement.querySelector('.help-box').getBoundingClientRect();
						// Calculate wrapper height without text
						const outerHeight = wrapRect.height - contentRect.height;
						// Calculate new text size from wrapper end to header end minus wrapper size
						const newSize = wrapRect.bottom - (headerRect.bottom + 40) - outerHeight;
						// Set new height
						const contentElm = this.el.nativeElement.querySelector('.tooltip-content');
						this.renderer.setStyle(contentElm, 'max-height', `${newSize}px`);
						// Get wrapper new height
						const marginTop = this.el.nativeElement.querySelectorAll('.help-box')[0].offsetHeight;
						// Set top margin on wraper
						const helpElm = this.el.nativeElement.querySelectorAll('.help-box')[0];
						this.renderer.setStyle(helpElm, 'margin-top', `-${marginTop}px`);
					});
				} else {
					// Scroll top minus tooltip height & top height
					/*zenscroll.toY(
            offset -
              (this.el.nativeElement
                .querySelector('.help-box')
                .getBoundingClientRect().height +
                headerRect.height +
                15)
          );*/
					zenscroll.toY(offset - (headerRect.bottom + 40));
				}
			} else {
				// Scroll to elemnt top
				zenscroll.toY(offset - (headerRect.bottom + 40));
			}
		}, 100);
	}

	resizeContent() {
		// Get element & footer positions & calculate max size
		const bodyRect = document.body.getBoundingClientRect();
		const elemRect = this.el.nativeElement.getBoundingClientRect();
		const headerRect = document.getElementsByTagName('header')[0].getBoundingClientRect();
		const footerRect = document.getElementsByTagName('footer')[0].getBoundingClientRect();

		// Get desktop max size (including outer wrapper)
		let maxSize = footerRect.top - elemRect.top;

		// Calculate mobile max size (including outer wrapper)
		if (
			(bowser.getParser(window.navigator.userAgent).getPlatformType() === 'mobile' ||
				bowser.getParser(window.navigator.userAgent).getPlatformType() === 'tablet') &&
			screen.width <= 1000
		) {
			maxSize = bodyRect.height - headerRect.height - 15;
		}

		// Get content element
		const contentArea = this.el.nativeElement.querySelectorAll('.tooltip-content')[0];
		// Reset max height
		contentArea.style.maxHeight = 'none';

		// Wait for tooltip to render new height
		setTimeout(() => {
			// Get tooltip wrapper
			const wrapArea = this.el.nativeElement.querySelectorAll('.help-box')[0];

			// Get content height without scrollbar
			const contentHeight = contentArea.offsetHeight;
			const wrapHeight = wrapArea.offsetHeight;
			// Add outer wrap height
			const outerHeight = wrapHeight - contentHeight;
			// Will be used to position tooltip above icon
			let finalHeight = 0;

			// Content height with outer height is smaller than max size
			// 10px addon is a treshold for almost equal heights
			if (maxSize + 10 > contentHeight + outerHeight) {
				// Allow to see all content
				this.renderer.setStyle(contentArea, 'max-height', `${contentHeight}px`);
				finalHeight = outerHeight + contentHeight;
			} else {
				// Limit content to max size
				contentArea.style.maxHeight = `${maxSize - outerHeight}px`;
				finalHeight = maxSize;
			}
			this.isMobile =
				bowser.getParser(window.navigator.userAgent).getPlatformType() === 'mobile' ||
				bowser.getParser(window.navigator.userAgent).getPlatformType() === 'tablet';
			// Handle mobile position
			if (this.isMobile && screen.width <= 1000) {
				if (wrapArea) {
					// wrapArea.style.marginTop = `-${finalHeight}px`;
				}
			}
		}, 100);
	}

	checkIfRatesTooltip() {
		// if we in tablet/mobile we want to display regular tooltip and not the small
		if (screen.width <= 1000) {
			return false;
		}

		const result = false;

		const compareTable = document.getElementsByTagName('comparison');
		const quoteSection = document.getElementsByTagName('quote');

		if (compareTable.length > 0 || quoteSection.length > 0) {
			// result = true;
		}
		return result;
	}

	showTooltip() {
		const tooltipContent = this.el.nativeElement.querySelectorAll('.tooltip-content')[0];
		if (tooltipContent && !this.isOpen) {
			tooltipContent.scrollTop = 0;
		}
		return this.tooltip && this.tooltip.content !== '';
	}

	toggleView() {
		if (document.body.offsetWidth <= 600) {
			this.tooltipService.open({
				data: {
					title: this.title,
					boxType: this.boxType,
					tooltip: this.tooltip,
					type: this.type,
					phoneNumber: this.phoneNumber,
					showPhoneNumber: this.showPhoneNumber,
					shouldShowCoverageHelp: this.shouldShowCoverageHelp,
				},
				windowClass: '',
				componentType: TooltipHandlerComponent,
			});
			if (this.gtmData) {
			}
		} else {
			if (this.focusLost && !this.isOpen && bowser.getParser(window.navigator.userAgent).getPlatformType() === 'ios') {
				this.focusLost = false;
				this.isOpen = true;
			}

			this.toggle();
		}
		this.sendgtmHelpPopupEvent();
		return false; // stoppropagation don't not remove
	}

	sendgtmHelpPopupEvent() {
		const globalAttributes = this.storeService.getGlobalAttributes();
	}

	toggle(event?: any) {
		this.tooltipService.openedTooltipSubject.next(this.tooltip);
		this.isOpen = !this.isOpen;
		if (this.isOpen && !this.checkIfRatesTooltip()) {
			setTimeout(() => {
				this.resizeContent();
			}, 0);
			this.scrollToTop();
		}

		if (!isEmpty(event) && event.keyCode === '13') {
			event.preventDefault();
		}
		if (this.gtmData) {
		}
	}

	focusLostCallback() {
		this.isOpen = false;
		this.focusLost = true;
	}

	getLabel(value) {
		return `Help_${this.title + value}`;
	}

	onKeyPress(event) {
		// if 'SPACE' or 'ENTER' key pressed, then open/close tooltip
		if (event.keyCode === 32 || event.keyCode === 13) {
			if (event.keyCode === 32) {
				event.preventDefault();
			}
			this.toggleView();
		}
	}

	isHovered() {
		this.splunkAnalyticsService.publish('Hover', this.control.attributes.title, '');
	}

	ngOnDestroy(): void {
		this.tooltipSubscription.unsubscribe();
	}
}
